/** Admin: Copy Trading. Lead-trader applications and statuses, and the copy engine's health. */
import { useState } from 'react';
import { D, fmtDateTime, fmtUsd } from '@platform/shared';
import { clientLabel } from '../components/ClientCell';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLoad } from '../stores/account';

interface Leader {
  userId: string;
  email: string;
  /** The client behind the public profile: profile name, else the CRM name; and the CRM id. */
  name: string | null;
  externalClientId: string | null;
  username: string;
  displayName: string;
  status: string;
  tier: string;
  markets: string[];
  strategy: string;
  appliedAt: string;
  decidedBy: string | null;
  decisionReason: string | null;
  copiers: number;
  aum: string;
  riskScore: number | null;
  return12m: string | null;
  maxDrawdown12m: string | null;
}

interface Health {
  signals_waiting: number;
  oldest_signal_sec: number | null;
  executions_pending: number;
  executions_retrying: number;
  skipped_24h: number;
  done_24h: number;
  p95_ms: number | null;
  skips: { reason: string; n: number }[];
}

const NEXT: Record<string, { to: string; label: string; tone: string }[]> = {
  applied: [
    { to: 'approved', label: 'Approve', tone: 'bg-up text-white' },
    { to: 'rejected', label: 'Reject', tone: 'border border-line-strong' },
  ],
  approved: [
    { to: 'suspended', label: 'Suspend', tone: 'border border-line-strong' },
    { to: 'retired', label: 'Retire', tone: 'border border-line-strong' },
  ],
  suspended: [
    { to: 'approved', label: 'Reinstate', tone: 'bg-up text-white' },
    { to: 'retired', label: 'Retire', tone: 'border border-line-strong' },
  ],
};

const pct = (v: string | null) => (v === null ? '—' : `${D.from(v).mul(100).toFixed(1)}%`);

export function CopyTab() {
  const [status, setStatus] = useState('applied');
  const { data, error, reload } = useLoad<Leader[]>(`/api/admin/copy/lead-traders${status ? `?status=${status}` : ''}`);
  const health = useLoad<Health>('/api/admin/copy/health');
  const [acting, setActing] = useState<{ l: Leader; to: string } | null>(null);
  const [reason, setReason] = useState('');
  const [tier, setTier] = useState('bronze');
  const [notice, setNotice] = useState('7');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const decide = async () => {
    if (!acting) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/api/admin/copy/lead-traders/${acting.l.userId}/status`, { status: acting.to, reason, tier: acting.to === 'approved' ? tier : undefined, noticeDays: Number(notice) });
      setActing(null);
      setReason('');
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const h = health.data;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {[
          ['Signals waiting', h ? `${h.signals_waiting}${h.oldest_signal_sec ? ` · ${h.oldest_signal_sec}s` : ''}` : '—', h && h.signals_waiting > 50],
          ['Executions pending', h?.executions_pending ?? '—', false],
          ['Retrying', h?.executions_retrying ?? '—', h && h.executions_retrying > 0],
          ['Copied · 24h', h?.done_24h ?? '—', false],
          ['Skipped · 24h', h?.skipped_24h ?? '—', false],
          ['p95 latency', h?.p95_ms != null ? `${h.p95_ms} ms` : '—', h && (h.p95_ms ?? 0) > 1000],
        ].map(([label, v, bad]) => (
          <div key={String(label)} className={cx('rounded-xl border bg-panel p-3', bad ? 'border-down' : 'border-line')}>
            <div className="text-[11px] text-muted">{label}</div>
            <div className="num mt-1 text-[17px] font-semibold">{String(v)}</div>
          </div>
        ))}
      </div>
      {!!h?.skips.length && <p className="m-0 text-xs text-muted">Skips (24h): {h.skips.map((s) => `${s.reason} ${s.n}`).join(' · ')}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {['applied', 'approved', 'suspended', 'rejected', 'retired', ''].map((s) => (
          <button key={s || 'all'} type="button" onClick={() => setStatus(s)} className={cx('h-8 rounded-full px-3 text-xs font-semibold', status === s ? 'bg-raised text-fg' : 'text-muted hover:text-fg')}>
            {s ? s[0]!.toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>
      {error && <p className="m-0 text-sm text-down">{error}</p>}
      {!data && !error && <Spinner />}
      {data && !data.length && <p className="m-0 text-sm text-muted">No lead traders here.</p>}
      <div className="flex flex-col gap-2">
        {data?.map((l) => (
          <div key={l.userId} className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-4 text-[13px]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="font-semibold">{l.displayName}</span> <span className="text-muted" title={l.email}>
                  @{l.username} · {clientLabel({ name: l.name, email: l.email, crmId: l.externalClientId })}
                </span>
                {!l.externalClientId && <span className="ml-1 rounded bg-raised px-1.5 py-px text-[10px] font-medium text-muted">No CRM ID</span>}
                <span className="ml-2 rounded-full bg-raised px-2 py-0.5 text-[11px] font-semibold">{l.status}</span>
                <span className="ml-1 text-[11px] text-muted uppercase">{l.tier}</span>
              </div>
              <div className="flex gap-2">
                {(NEXT[l.status] ?? []).map((n) => (
                  <button key={n.to} type="button" onClick={() => setActing({ l, to: n.to })} className={cx('h-8 rounded-lg px-3 text-xs font-semibold', n.tone)}>
                    {n.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="num flex flex-wrap gap-4 text-xs text-muted">
              <span>Copiers {l.copiers}</span>
              <span>AUM {fmtUsd(l.aum)}</span>
              <span>Risk {l.riskScore ?? '—'}</span>
              <span>12M {pct(l.return12m)}</span>
              <span>Max DD {pct(l.maxDrawdown12m)}</span>
              <span>Markets {l.markets.join(', ')}</span>
              <span>Applied {fmtDateTime(l.appliedAt)}</span>
            </div>
            <p className="m-0 text-xs whitespace-pre-line">{l.strategy}</p>
            {l.decisionReason && (
              <p className="m-0 text-xs text-muted">
                Last decision: {l.decisionReason} {l.decidedBy ? `(${l.decidedBy})` : ''}
              </p>
            )}
            {acting?.l.userId === l.userId && (
              <div className="flex flex-wrap items-end gap-2 border-t border-line pt-3">
                <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-[11px] text-muted">
                  Reason (kept in the audit and sent to the CRM)
                  <input id={`lt-reason-${l.userId}`} value={reason} onChange={(e) => setReason(e.target.value)} className="h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none" />
                </label>
                {acting.to === 'approved' && (
                  <label className="flex flex-col gap-1 text-[11px] text-muted">
                    Tier
                    <select id={`lt-tier-${l.userId}`} value={tier} onChange={(e) => setTier(e.target.value)} className="h-9 rounded-lg border border-line bg-bg px-2 text-[13px] text-fg">
                      {['bronze', 'silver', 'gold', 'platinum'].map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                )}
                {(acting.to === 'suspended' || acting.to === 'retired') && (
                  <label className="flex flex-col gap-1 text-[11px] text-muted">
                    Copies stop after (days)
                    <input id={`lt-notice-${l.userId}`} value={notice} onChange={(e) => setNotice(e.target.value.replace(/\D/g, ''))} className="h-9 w-24 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg" />
                  </label>
                )}
                <button type="button" disabled={busy || !reason.trim()} onClick={() => void decide()} className="h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50">
                  {busy ? 'Saving…' : `Confirm: ${acting.to}`}
                </button>
                <button type="button" onClick={() => setActing(null)} className="h-9 px-2 text-xs text-muted">
                  Cancel
                </button>
                {msg && <span className="w-full text-xs text-down">{msg}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
